import { useState } from "react";
import { Loader2, CheckCircle2, AlertCircle, MessageCircle } from "lucide-react";
import { site } from "../config/site";
import { services } from "../data/services";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PHONE_RE = /^[+0-9][0-9\s\-()]{7,17}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const empty = {
  name: "",
  phone: "",
  email: "",
  preferred_date: "",
  preferred_time: "",
  service: "",
  message: "",
};

export default function EnquiryForm({ variant = "contact", preselectedService = "" }) {
  const isBooking = variant === "booking";
  const [form, setForm] = useState({ ...empty, service: preselectedService });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [serverMsg, setServerMsg] = useState("");

  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (form.name.trim().length < 2) er.name = "Please enter your name.";
    if (!PHONE_RE.test(form.phone.trim()))
      er.phone = "Please enter a valid phone number.";
    if (form.email && !EMAIL_RE.test(form.email.trim()))
      er.email = "Please enter a valid email address.";
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (status === "sending") return;
    if (!validate()) return;
    setStatus("sending");
    setServerMsg("");
    try {
      const res = await fetch(`${API}/enquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          phone: form.phone.trim(),
          email: form.email.trim() || null,
          preferred_date: form.preferred_date || null,
          preferred_time: form.preferred_time || null,
          service: form.service || null,
          message: form.message.trim() || null,
          source: variant,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        const detail = data?.detail;
        throw new Error(
          typeof detail === "string"
            ? detail
            : Array.isArray(detail)
              ? detail[0]?.msg
              : "Something went wrong. Please try again or call us directly.",
        );
      }
      setServerMsg(data.message);
      setStatus("success");
    } catch (err) {
      setServerMsg(
        err.message || "Something went wrong. Please try again or call us directly.",
      );
      setStatus("error");
    }
  };

  const whatsappFollowUp = `${site.whatsappUrl}?text=${encodeURIComponent(
    `Hello Glow & Blow, I just sent an enquiry via the website. Name: ${form.name}. ${form.service ? `Service: ${form.service}.` : ""}`,
  )}`;

  if (status === "success") {
    return (
      <div
        className="border border-champagne/50 bg-champagne/10 p-8 md:p-10"
        data-testid="enquiry-success"
        role="status"
      >
        <CheckCircle2 className="h-8 w-8 text-champagne-dark" />
        <h3 className="mt-4 font-display text-2xl font-medium text-ink md:text-3xl">
          Thank you, {form.name.split(" ")[0]}.
        </h3>
        <p className="mt-3 max-w-md text-sm leading-relaxed text-soften md:text-base">
          {serverMsg ||
            "Thank you. Your enquiry has been received. We'll get back to you shortly."}
        </p>
        {isBooking && (
          <p className="mt-2 text-xs italic text-taupe">
            Appointment requests are subject to availability.
          </p>
        )}
        <div className="mt-7 flex flex-wrap gap-3">
          <a
            href={whatsappFollowUp}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="enquiry-whatsapp-followup"
            className="inline-flex min-h-[48px] items-center gap-2 bg-ink px-6 text-[13px] font-semibold uppercase tracking-[0.14em] text-white transition-colors hover:bg-taupe"
          >
            <MessageCircle className="h-4 w-4 text-champagne" />
            Continue on WhatsApp
          </a>
          <button
            type="button"
            data-testid="enquiry-send-another"
            onClick={() => {
              setForm({ ...empty });
              setStatus("idle");
            }}
            className="inline-flex min-h-[48px] items-center border border-ink px-6 text-[13px] font-semibold uppercase tracking-[0.14em] text-ink transition-colors hover:bg-ink hover:text-white"
          >
            Send Another
          </button>
        </div>
      </div>
    );
  }

  const field = (key, label, node, required = false) => (
    <div>
      <label htmlFor={`${variant}-${key}`} className="field-label">
        {label} {required && <span className="text-champagne-dark">*</span>}
      </label>
      {node}
      {errors[key] && (
        <p className="mt-2 flex items-center gap-1.5 text-xs text-red-700" role="alert" data-testid={`${variant}-${key}-error`}>
          <AlertCircle className="h-3.5 w-3.5" />
          {errors[key]}
        </p>
      )}
    </div>
  );

  const inputCls = (key) =>
    `field-input ${errors[key] ? "border-red-400" : ""}`;

  return (
    <form onSubmit={submit} noValidate data-testid={`${variant}-form`} className="space-y-6">
      {status === "error" && (
        <div
          className="flex items-start gap-3 border border-red-300 bg-red-50 p-4 text-sm text-red-800"
          role="alert"
          data-testid="enquiry-error"
        >
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          {serverMsg}
        </div>
      )}

      <div className="grid gap-6 sm:grid-cols-2">
        {field(
          "name",
          isBooking ? "Full Name" : "Name",
          <input
            id={`${variant}-name`}
            data-testid={`${variant}-name-input`}
            type="text"
            autoComplete="name"
            value={form.name}
            onChange={set("name")}
            className={inputCls("name")}
            placeholder="Your name"
          />,
          true,
        )}
        {field(
          "phone",
          "Phone",
          <input
            id={`${variant}-phone`}
            data-testid={`${variant}-phone-input`}
            type="tel"
            autoComplete="tel"
            value={form.phone}
            onChange={set("phone")}
            className={inputCls("phone")}
            placeholder="+91 ..."
          />,
          true,
        )}
      </div>

      {!isBooking &&
        field(
          "email",
          "Email (optional)",
          <input
            id={`${variant}-email`}
            data-testid={`${variant}-email-input`}
            type="email"
            autoComplete="email"
            value={form.email}
            onChange={set("email")}
            className={inputCls("email")}
            placeholder="you@example.com"
          />,
        )}

      <div className="grid gap-6 sm:grid-cols-2">
        {field(
          "preferred_date",
          "Preferred Date",
          <input
            id={`${variant}-date`}
            data-testid={`${variant}-date-input`}
            type="date"
            value={form.preferred_date}
            onChange={set("preferred_date")}
            className={inputCls("preferred_date")}
          />,
        )}
        {field(
          "preferred_time",
          "Preferred Time",
          <input
            id={`${variant}-time`}
            data-testid={`${variant}-time-input`}
            type="time"
            value={form.preferred_time}
            onChange={set("preferred_time")}
            className={inputCls("preferred_time")}
          />,
        )}
      </div>

      {field(
        "service",
        "Service",
        <select
          id={`${variant}-service`}
          data-testid={`${variant}-service-select`}
          value={form.service}
          onChange={set("service")}
          className={inputCls("service")}
        >
          <option value="">Select a service (optional)</option>
          {services.map((s) => (
            <option key={s.id} value={s.name}>
              {s.name}
            </option>
          ))}
          <option value="Not sure yet">Not sure yet</option>
        </select>,
      )}

      {field(
        "message",
        "Message",
        <textarea
          id={`${variant}-message`}
          data-testid={`${variant}-message-input`}
          rows={4}
          value={form.message}
          onChange={set("message")}
          className={`${inputCls("message")} min-h-[120px] resize-y`}
          placeholder={
            isBooking
              ? "Anything we should know before your visit?"
              : "How can we help?"
          }
        />,
      )}

      <button
        type="submit"
        disabled={status === "sending"}
        data-testid={`${variant}-submit-button`}
        className="inline-flex min-h-[52px] w-full items-center justify-center gap-2.5 bg-ink px-8 text-sm font-semibold uppercase tracking-[0.14em] text-white transition-colors duration-300 hover:bg-taupe disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto"
      >
        {status === "sending" ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            Sending...
          </>
        ) : isBooking ? (
          "Request Appointment"
        ) : (
          "Send Enquiry"
        )}
      </button>

      {isBooking && (
        <p className="text-xs italic text-soften">
          Appointment requests are subject to availability.
        </p>
      )}
    </form>
  );
}
